import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiX, FiPlus, FiCheck, FiAlertCircle, FiRefreshCw, FiList, FiCpu } from 'react-icons/fi';
import CartService from '../../Utils/API/cart';

export default function QuickOrderModal({ isOpen, onClose, buttonRef }) {
    const [textInput, setTextInput] = useState('');
    const [processing, setProcessing] = useState(false);
    const [addingToCart, setAddingToCart] = useState(false);
    const [results, setResults] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState({});
    const [expandedAlternatives, setExpandedAlternatives] = useState({});
    const modalRef = useRef(null);

    const modalPosition = buttonRef?.current?.getBoundingClientRect();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }, 100);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    const processQuickOrder = async () => {
        if (!textInput.trim()) {
            toast.error('Por favor, pega una lista de productos');
            return;
        }

        setProcessing(true);
        try {
            const response = await axios.post('https://api.rb.beeapp.es/products/quick-order', {
                textInput
            }, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            setResults(response.data.results);

            // Auto-seleccionar el primer match con mayor confianza
            const initialSelections = {};
            response.data.results.forEach((result, index) => {
                if (result.matches && result.matches.length > 0) {
                    initialSelections[index] = {
                        product: result.matches[0],
                        quantity: result.matches[0].adjustedQuantity || result.requestedQuantity || 1
                    };
                }
            });
            setSelectedProducts(initialSelections);

        } catch (error) {
            console.error('Error processing quick order:', error);
            toast.error('Error al procesar el pedido rápido');
        } finally {
            setProcessing(false);
        }
    };

    const selectProduct = (resultIndex, product) => {
        setSelectedProducts(prev => ({
            ...prev,
            [resultIndex]: {
                product,
                quantity: product.adjustedQuantity || prev[resultIndex]?.quantity || 1
            }
        }));
    };

    const quantityTimeouts = useRef({});

    const updateQuantity = (resultIndex, quantity) => {
        const product = selectedProducts[resultIndex]?.product;
        if (!product) return;

        const rawQuantity = parseInt(quantity) || 1;

        // Actualizar inmediatamente con el valor ingresado
        setSelectedProducts(prev => ({
            ...prev,
            [resultIndex]: {
                ...prev[resultIndex],
                quantity: rawQuantity
            }
        }));

        // Limpiar timeout anterior
        if (quantityTimeouts.current[resultIndex]) {
            clearTimeout(quantityTimeouts.current[resultIndex]);
        }

        // Ajustar después de 600ms si es necesario
        quantityTimeouts.current[resultIndex] = setTimeout(() => {
            const quantityStep = product.quantityStep || 1;
            const stock = product.stock || 0;
            let adjustedQuantity = rawQuantity;

            // Solo ajustar a múltiplo de quantityStep si el stock >= quantityStep
            if (stock >= quantityStep && rawQuantity % quantityStep !== 0) {
                adjustedQuantity = Math.ceil(rawQuantity / quantityStep) * quantityStep;

                setSelectedProducts(prev => ({
                    ...prev,
                    [resultIndex]: {
                        ...prev[resultIndex],
                        quantity: adjustedQuantity
                    }
                }));

                if (adjustedQuantity !== rawQuantity) {
                    toast.info(`Cantidad ajustada a ${adjustedQuantity} (múltiplo de ${quantityStep})`, {
                        autoClose: 2000
                    });
                }
            }
        }, 600);
    };

    const addAllToCart = async () => {
        const productsToAdd = Object.values(selectedProducts).filter(item => item.product);

        if (productsToAdd.length === 0) {
            toast.error('No hay productos seleccionados');
            return;
        }

        setAddingToCart(true);

        try {
            const products = productsToAdd.map(item => ({
                id: item.product._id,
                quantity: item.quantity
            }));

            const result = await CartService.addMultipleProductsToCart(products);

            if (result.success) {
                // Despachar evento personalizado para actualizar el carrito
                window.dispatchEvent(new Event('cartUpdated'));
                onClose();
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error('Error al añadir productos al carrito');
        } finally {
            setAddingToCart(false);
        }
    };

    // Usar las líneas del usuario para la animación de carga
    const userLines = textInput.split('\n').filter(line => line.trim() !== '');
    const loadingProducts = userLines.length > 0
        ? [...userLines, ...userLines]
        : ['Cargando...', 'Procesando...', 'Analizando...'].map((text, i) => text);

    return (
        <>
            <style>
                {`
                    @keyframes marqueeUp {
                        0% {
                            transform: translateY(0);
                        }
                        100% {
                            transform: translateY(-50%);
                        }
                    }

                    @keyframes fadeInSlideUp {
                        from {
                            opacity: 0;
                            transform: translateY(10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.5;
                        }
                    }

                    @keyframes spin {
                        0% {
                            transform: rotate(0deg);
                        }
                        100% {
                            transform: rotate(360deg);
                        }
                    }

                    .product-item-enter {
                        animation: fadeInSlideUp 0.3s ease-out;
                    }

                    .marquee-container {
                        position: relative;
                        height: 300px;
                        overflow: hidden;
                        mask-image: linear-gradient(to bottom,
                            transparent,
                            black 20%,
                            black 80%,
                            transparent
                        );
                        -webkit-mask-image: linear-gradient(to bottom,
                            transparent,
                            black 20%,
                            black 80%,
                            transparent
                        );
                    }

                    .marquee-content {
                        animation: marqueeUp 10s linear infinite;
                    }

                    .loading-shimmer {
                        background: linear-gradient(
                            90deg,
                            #f0f0f0 25%,
                            #e0e0e0 50%,
                            #f0f0f0 75%
                        );
                        background-size: 200% 100%;
                        animation: shimmer 1.5s infinite;
                    }

                    @keyframes shimmer {
                        0% {
                            background-position: -200% 0;
                        }
                        100% {
                            background-position: 200% 0;
                        }
                    }
                `}
            </style>
            <div
                ref={modalRef}
                style={{
                    position: 'absolute',
                    top: modalPosition ? `${modalPosition.bottom + 10}px` : '85px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '90vw',
                    maxWidth: '800px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    zIndex: 1000,
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '24px 32px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#fafafa'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h3 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#111827',
                                margin: 0
                            }}>
                                Pedido Rápido por Listado
                            </h3>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '6px',
                                padding: '4px 10px'
                            }}>
                                <FiCpu size={14} color="#028322" />
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#028322',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    IA
                                </span>
                            </div>
                        </div>
                        <p style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            margin: 0,
                            marginTop: '6px'
                        }}>
                            Reconocimiento inteligente de productos con IA
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    >
                        <FiX size={20} color="#6b7280" />
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    padding: '32px',
                    backgroundColor: '#fff'
                }}>
                    {processing ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '24px'
                        }}>
                            <div className="marquee-container" style={{
                                width: '100%',
                                borderRadius: '12px',
                                backgroundColor: '#fff',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div className="marquee-content">
                                    {loadingProducts.map((line, index) => (
                                        <div key={index} style={{
                                            padding: '16px 20px',
                                            display: 'flex',
                                            gap: '16px',
                                            alignItems: 'center',
                                            borderBottom: '1px solid #e5e7eb'
                                        }}>
                                            <div className="loading-shimmer" style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '8px'
                                            }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: '#111827',
                                                    fontFamily: 'monospace',
                                                    marginBottom: '4px'
                                                }}>
                                                    {line}
                                                </div>
                                                <div className="loading-shimmer" style={{
                                                    height: '12px',
                                                    width: '40%',
                                                    borderRadius: '4px'
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#028322',
                                    animation: 'pulse 1.5s ease-in-out infinite'
                                }} />
                                <span style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    color: '#6b7280'
                                }}>
                                    Procesando con IA...
                                </span>
                            </div>
                        </div>
                    ) : results.length === 0 ? (
                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div style={{
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #bbf7d0',
                                borderRadius: '12px',
                                padding: '16px 20px',
                                marginBottom: '24px',
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'flex-start'
                            }}>
                                <FiList size={18} color="#028322" style={{ marginTop: '2px' }} />
                                <div style={{ flex: 1 }}>
                                    <p style={{
                                        fontSize: '14px',
                                        color: '#166534',
                                        margin: 0,
                                        marginBottom: '12px',
                                        fontWeight: '500'
                                    }}>
                                        Formatos aceptados:
                                    </p>
                                    <div style={{
                                        backgroundColor: '#dcfce7',
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#166534',
                                        fontFamily: 'monospace',
                                        lineHeight: '1.8'
                                    }}>
                                        0028137 5<br />
                                        BOLIGRAFO BIC AZUL x3<br />
                                        22349 | Silla oficina | 2<br />
                                        EAN: 8412345678901 - 10 uds
                                    </div>
                                </div>
                            </div>

                            <textarea
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                                placeholder="Pega aquí tu lista de productos..."
                                style={{
                                    width: '100%',
                                    minHeight: '250px',
                                    padding: '16px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '12px',
                                    fontSize: '14px',
                                    fontFamily: 'monospace',
                                    resize: 'vertical',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    backgroundColor: '#fff',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#028322'}
                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            />

                            <button
                                onClick={processQuickOrder}
                                disabled={!textInput.trim()}
                                style={{
                                    marginTop: '20px',
                                    width: '100%',
                                    padding: '14px',
                                    backgroundColor: textInput.trim() ? '#028322' : '#d1d5db',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: textInput.trim() ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                    if (textInput.trim()) {
                                        e.currentTarget.style.backgroundColor = '#026b1d';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (textInput.trim()) {
                                        e.currentTarget.style.backgroundColor = '#028322';
                                    }
                                }}
                            >
                                <FiPlus size={18} />
                                Procesar Listado
                            </button>
                        </div>
                    ) : (
                        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '24px',
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div>
                                    <h4 style={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        color: '#111827',
                                        margin: 0,
                                        marginBottom: '4px'
                                    }}>
                                        Productos encontrados
                                    </h4>
                                    <p style={{
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        margin: 0
                                    }}>
                                        {results.length} {results.length === 1 ? 'producto detectado' : 'productos detectados'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setResults([]);
                                        setSelectedProducts({});
                                        setExpandedAlternatives({});
                                        setTextInput('');
                                    }}
                                    style={{
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        padding: '6px 12px',
                                        cursor: 'pointer',
                                        fontWeight: '500',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                        e.currentTarget.style.borderColor = '#d1d5db';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#fff';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                    }}
                                >
                                    <FiRefreshCw size={14} />
                                    Nueva búsqueda
                                </button>
                            </div>

                            {results.map((result, index) => (
                                <div key={index} className="product-item-enter" style={{
                                    marginBottom: '16px',
                                    padding: '20px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    backgroundColor: '#fafafa',
                                    animationDelay: `${index * 0.05}s`
                                }}>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        marginBottom: '12px',
                                        fontFamily: 'monospace',
                                        backgroundColor: '#fff',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        {result.originalLine}
                                    </div>

                                    {result.matches && result.matches.length > 0 ? (
                                        <>
                                            {selectedProducts[index]?.product && (
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '16px',
                                                    padding: '16px',
                                                    backgroundColor: '#f0fdf4',
                                                    border: '2px solid #028322',
                                                    borderRadius: '12px',
                                                    marginBottom: expandedAlternatives[index] ? '16px' : '0'
                                                }}>
                                                    <img
                                                        src={selectedProducts[index].product.images?.[0]}
                                                        alt={selectedProducts[index].product.name}
                                                        style={{
                                                            width: '70px',
                                                            height: '70px',
                                                            objectFit: 'cover',
                                                            borderRadius: '8px',
                                                            border: '1px solid #bbf7d0'
                                                        }}
                                                    />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: '#111827',
                                                            marginBottom: '6px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            {selectedProducts[index].product.name}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '13px',
                                                            color: '#6b7280',
                                                            marginBottom: '12px'
                                                        }}>
                                                            Ref: {selectedProducts[index].product.reference} • <span style={{ fontWeight: '600', color: '#028322' }}>{selectedProducts[index].product.price}€</span>
                                                        </div>
                                                        <div>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px'
                                                            }}>
                                                                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Cantidad:</span>
                                                                <input
                                                                    type="number"
                                                                    value={selectedProducts[index]?.quantity || 1}
                                                                    onChange={(e) => updateQuantity(index, e.target.value)}
                                                                    min="1"
                                                                    style={{
                                                                        width: '70px',
                                                                        padding: '6px 10px',
                                                                        border: '1px solid #d1d5db',
                                                                        borderRadius: '8px',
                                                                        fontSize: '13px',
                                                                        fontWeight: '500',
                                                                        outline: 'none'
                                                                    }}
                                                                />
                                                            </div>
                                                            {selectedProducts[index]?.product?.adjustedQuantity &&
                                                             selectedProducts[index]?.product?.adjustedQuantity !== result.requestedQuantity && (
                                                                <div style={{
                                                                    marginTop: '8px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    fontSize: '11px',
                                                                    color: '#f59e0b',
                                                                    backgroundColor: '#fef3c7',
                                                                    padding: '4px 8px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #fcd34d'
                                                                }}>
                                                                    <FiAlertCircle size={12} />
                                                                    <span>
                                                                        Ajustada a múltiplo de {selectedProducts[index].product.quantityStep || 1}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                                                        <div style={{
                                                            backgroundColor: '#028322',
                                                            borderRadius: '50%',
                                                            width: '32px',
                                                            height: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FiCheck size={18} color="#fff" />
                                                        </div>
                                                        {result.matches.length > 1 && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setExpandedAlternatives(prev => ({
                                                                        ...prev,
                                                                        [index]: !prev[index]
                                                                    }));
                                                                }}
                                                                style={{
                                                                    background: '#fff',
                                                                    border: '1px solid #d1d5db',
                                                                    borderRadius: '8px',
                                                                    padding: '8px',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'all 0.2s'
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                                                    e.currentTarget.style.borderColor = '#028322';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#fff';
                                                                    e.currentTarget.style.borderColor = '#d1d5db';
                                                                }}
                                                            >
                                                                <FiRefreshCw size={16} color="#6b7280" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {expandedAlternatives[index] && result.matches.length > 1 && (
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    flexWrap: 'wrap',
                                                    padding: '16px',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '12px',
                                                    border: '1px solid #e5e7eb'
                                                }}>
                                                    {result.matches.slice(1).map((match, matchIndex) => (
                                                        <div
                                                            key={matchIndex}
                                                            onClick={() => {
                                                                selectProduct(index, match);
                                                                setExpandedAlternatives(prev => ({
                                                                    ...prev,
                                                                    [index]: false
                                                                }));
                                                            }}
                                                            style={{
                                                                flex: '1 1 calc(50% - 6px)',
                                                                display: 'flex',
                                                                gap: '12px',
                                                                padding: '12px',
                                                                backgroundColor: '#fafafa',
                                                                border: '1px solid #e5e7eb',
                                                                borderRadius: '10px',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.borderColor = '#028322';
                                                                e.currentTarget.style.backgroundColor = '#f0fdf4';
                                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                                e.currentTarget.style.backgroundColor = '#fafafa';
                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                            }}
                                                        >
                                                            <img
                                                                src={match.images?.[0]}
                                                                alt={match.name}
                                                                style={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '8px'
                                                                }}
                                                            />
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: '600',
                                                                    color: '#111827',
                                                                    marginBottom: '4px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {match.name}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '12px',
                                                                    color: '#6b7280'
                                                                }}>
                                                                    Ref: {match.reference} • {match.price}€
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '16px',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '10px',
                                            border: '1px solid #fbbf24'
                                        }}>
                                            <FiAlertCircle size={20} color="#f59e0b" />
                                            <span style={{ fontSize: '13px', color: '#92400e', fontWeight: '500' }}>
                                                No se encontraron productos que coincidan
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}

                            <button
                                onClick={addAllToCart}
                                disabled={addingToCart}
                                style={{
                                    marginTop: '24px',
                                    width: '100%',
                                    padding: '16px',
                                    backgroundColor: addingToCart ? '#9ca3af' : '#028322',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: addingToCart ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.2s',
                                    boxShadow: addingToCart ? 'none' : '0 4px 6px -1px rgba(2, 131, 34, 0.3)',
                                    opacity: addingToCart ? 0.7 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!addingToCart) {
                                        e.currentTarget.style.backgroundColor = '#026b1d';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(2, 131, 34, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!addingToCart) {
                                        e.currentTarget.style.backgroundColor = '#028322';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(2, 131, 34, 0.3)';
                                    }
                                }}
                            >
                                {addingToCart ? (
                                    <>
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            border: '3px solid #fff',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite'
                                        }} />
                                        Añadiendo...
                                    </>
                                ) : (
                                    <>
                                        <FiPlus size={20} />
                                        Añadir {Object.keys(selectedProducts).length} {Object.keys(selectedProducts).length === 1 ? 'producto' : 'productos'} al carrito
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
